<template>
  <div class="product-coupon-modal">
    <bp-input
      v-model="form.code"
      class="mb-3"
      id="code"
      required
      label="Nome do cupom"
      placeholder="Qual o nome do cupom? Ex: VELORA10"
      description="Defina o nome do cupom que seus clientes irão usar no checkout. Importante: seus clientes deverão escrever exatamente como está neste campo para validar o cupom."
      :errors="errors.code"
      :limit="40"
    />

    <div class="row">
      <div class="col-lg-6">
        <bp-input
          v-model="form.discount"
          money
          required
          whole-number
          class="mb-3"
          label="Valor do cupom"
          description="Atenção: Este valor não deve ser maior nem igual do que o valor da oferta."
          :min="0"
          :errors="errors.discount"
        />
      </div>
      <div class="col-lg-6">
        <bp-input
          v-model="form.limit_max"
          number
          whole-number
          outerButtons
          label="Limite de usos"
          required
          placeholder="Limite de vezes que esse cupom pode ser utilizado"
          description="Digite um número inteiro para o limite de usos do seu cupom."
          :min="1"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <form-group
          class="mb-3"
          v-model="form.start_at"
          readonly
          is-date
          required
          id="date"
          mode="dateTime"
          label="Início da validade do cupom"
          placeholder="dd/mm/yyyy"
          :errors="errors.start_at"
          :min-date="new Date()"
        />
      </div>
      <div class="col-lg-6">
        <form-group
          class="mb-3"
          v-model="form.end_at"
          readonly
          is-date
          required
          id="date"
          mode="dateTime"
          label="Fim da validade do cupom"
          placeholder="dd/mm/yyyy"
          :errors="errors.start_at"
          :min-date="minEndDate"
        />
      </div>
    </div>

    <action-dispatcher
      :action="product.type_product == 'signature' ? 'plan/get' : 'offer/get'"
      :parameters="{ product_id: $route.params.id, limit: 100 }"
    >
      <form-group
        v-model="form.itens"
        required
        select
        multiple
        id="itens"
        class="p-0"
        placeholder="Selecionar"
        :label="
          product.type_product == 'signature'
            ? 'Selecionar planos'
            : 'Selecionar ofertas'
        "
        :options="product.type_product == 'signature' ? plans : offers"
      />
      <div class="functional-1 text-neutral-600 mt-1 mb-5">
        Selecione
        {{
          product.type_product == "signature"
            ? "os planos aos"
            : "as ofertas às"
        }}
        quais seu cupom se aplicará. Certifique-se de que o valor final no
        checkout, após aplicado o cupom, seja sempre maior que R$ 4,00.
      </div>
    </action-dispatcher>

    <bp-data-table
      v-if="selectedItems.length"
      :data="selectedItems"
      :columns="columns"
    >
    </bp-data-table>

    <div class="d-flex flex-column flex-lg-row justify-content-between mt-9">
      <bp-button
        size="xl"
        label="Salvar"
        class="order-lg-1"
        :loading="loading"
        :disabled="!isValid"
        @click="submit"
      />

      <bp-button
        size="xl"
        color="neutral"
        label="Cancelar"
        variant="filled-1"
        class="mt-3 mt-lg-0"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>

<script>
import {
  clearObject,
  formatMoney,
  formFields,
  toComputerDate
} from "@/functions/helpers";
import { mapState } from "vuex";
export default {
  props: {
    coupon: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      ...formFields(
        [
          "id",
          "code",
          "product_id",
          "start_at",
          "end_at",
          "discount",
          "limit_max",
          "itens",
          "is_active"
        ],
        {
          product_id: this.$route.params.id,
          is_active: true,
          ...this.coupon,
          itens: [
            ...(this.coupon?.itens || []).map(i => i.offer_id || i.plan_id)
          ]
        }
      ),
      loading: false
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("offer", ["offers"]),
    ...mapState("plan", ["plans"]),
    minEndDate() {
      if (this.form.start_at)
        return new Date(toComputerDate(this.form.start_at));

      return new Date();
    },
    isValid() {
      return (
        this.form.code &&
        this.form.code.length <= 40 &&
        this.form.start_at &&
        this.form.end_at &&
        this.form.limit_max > 0 &&
        !(this.form.limit_max % 1) &&
        this.form.discount > 0 &&
        !(this.form.discount % 1) &&
        this.form.itens.length
      );
    },
    selectedItems() {
      const options =
        this.product.type_product == "signature" ? this.plans : this.offers;
      return [
        ...this.form.itens
          .map(i => ({
            ...options.find(opt => opt.id == i),
            discount: this.form.discount
          }))
          .filter(i => !!i.name)
      ];
    },
    columns() {
      return [
        {
          name:
            this.product.type_product == "signature"
              ? "Nome do plano"
              : "Nome da oferta",
          field: "name"
        },
        {
          name:
            this.product.type_product == "signature"
              ? "Valor do plano"
              : "Valor da oferta",
          field: "price",
          transform: v => `R$ ${formatMoney(v)}`
        },
        {
          name: "Desconto",
          field: "discount",
          transform: v => `R$ ${formatMoney(v)}`,
          class: "text-negative-600"
        },
        {
          name: "Total",
          field: "price",
          transform: v => `R$ ${formatMoney(v - this.form.discount)}`
        }
      ];
    }
  },
  methods: {
    submit() {
      const data = { ...this.form };

      const action = data.id ? "product/editCoupon" : "product/postCoupon";

      if (data.itens.length) {
        const type =
          this.product.type_product == "signature" ? "plan_id" : "offer_id";
        data.itens = data.itens.map(i => ({ [type]: i }));
      }

      data.start_at = toComputerDate(data.start_at);
      data.end_at = toComputerDate(data.end_at);

      this.loading = true;
      this.$store
        .dispatch(action, clearObject(data))
        .then(() => {
          this.$message.success("Cupom salvo");
          this.$emit("close");
          this.$emit("refresh");
        })
        .catch(error => {
          this.$message.error(
            error.response?.data?.message || "Erro ao salvar cupom"
          );
        })
        .finally(() => (this.loading = false));
    }
  },
  watch: {
    "form.start_at"(v) {
      if (
        this.form.end_at &&
        new Date(toComputerDate(v)) > new Date(toComputerDate(this.form.end_at))
      ) {
        this.form.end_at = "";
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.product-coupon-modal {
  ::v-deep.form-group {
    label {
      font-size: 14px !important;
    }
  }
}
</style>
